import { differenceInCalendarDays, format, isValid } from 'date-fns';

/**
 * SW2-2467. Phone-layout helpers for the My Projects widget.
 *
 * The desktop table has no due-status model: it shows a CTA label from `getCtaText`
 * (InspectionsDue.jsx). The phone cards add a status tag and filter tiles on top of the same rows,
 * so the status is derived here, from those rows' `dueDate` / `inspectionStatus`. Nothing here
 * decides where an action goes — routes and handlers stay with the desktop code.
 *
 * Dates are parsed by the widget's own `parseDueDateLocal` (passed in as `parse`) so a due date means
 * the same local day on both layouts. A due date that does not parse — the API can send the literal
 * "Invalid date" — is treated as no date: date-fns `format` throws on it, and an unguarded
 * `differenceInCalendarDays` returns NaN, which would count the row as upcoming.
 */

export type DueRow = {
  siteName?: string;
  clientName?: string;
  dueDate?: string | null;
  inspectionStatus?: string;
  inspectionId?: number | null;
  inspection_template_name?: string | null;
  inspection_template_type?: string | null;
  [key: string]: any;
};

export type ParseDueDate = (dueDate: string) => Date;

export type FilterKey = 'all' | 'overdue' | 'dueToday' | 'upcoming' | 'noDate';

export type SortKey = 'dueAsc' | 'dueDesc' | 'site' | 'client' | 'form';

export type TagTone = 'overdue' | 'inProgress' | 'dueToday' | 'upcoming' | 'none';

export const FILTER_OPTIONS: { key: FilterKey; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'dueToday', label: 'Due today' },
  { key: 'upcoming', label: 'Upcoming' },
  // Forms assigned to the user with no next or scheduled date, so the tiles add up to All.
  { key: 'noDate', label: 'No date' },
];

export const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: 'dueAsc', label: 'Oldest due first' },
  { key: 'dueDesc', label: 'Newest due first' },
  { key: 'site', label: 'Site A–Z' },
  { key: 'client', label: 'Client A–Z' },
  { key: 'form', label: 'Form A–Z' },
];

/** The calendar's agenda is one day, so due date cannot order it. */
export const CALENDAR_SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: 'site', label: 'Site A–Z' },
  { key: 'client', label: 'Client A–Z' },
  { key: 'form', label: 'Form A–Z' },
];

/** What the desktop Projects table sorts by. */
export const PROJECT_SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: 'site', label: 'Site A–Z' },
  { key: 'client', label: 'Client A–Z' },
];

/** The row's due date, or null when it has none or it does not parse. */
function parseDue(row: DueRow, parse: ParseDueDate): Date | null {
  if (!row.dueDate) return null;

  const date = parse(row.dueDate);

  return isValid(date) ? date : null;
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** An inspection exists and is not finished: started/answered, or created but not opened yet. */
export function isInProgress(row: DueRow): boolean {
  return (
    row.inspectionStatus === 'started' ||
    row.inspectionStatus === 'answered' ||
    (row.inspectionStatus === 'new' && Number(row.inspectionId) > 0)
  );
}

export function getDueBucket(
  row: DueRow,
  parse: ParseDueDate,
  now: Date,
): { bucket: Exclude<FilterKey, 'all' | 'noDate'> | 'none'; days: number } {
  const due = parseDue(row, parse);

  if (!due) {
    return { bucket: 'none', days: 0 };
  }

  const diff = differenceInCalendarDays(due, now);

  if (diff < 0) return { bucket: 'overdue', days: -diff };
  if (diff === 0) return { bucket: 'dueToday', days: 0 };

  return { bucket: 'upcoming', days: diff };
}

export function getStatusTag(
  row: DueRow,
  parse: ParseDueDate,
  now: Date,
): { tone: TagTone; label: string } {
  const { bucket, days } = getDueBucket(row, parse, now);

  if (isInProgress(row)) {
    return {
      tone: 'inProgress',
      label: bucket === 'overdue' ? `In progress · ${days}d late` : 'In progress',
    };
  }

  switch (bucket) {
    case 'overdue':
      return { tone: 'overdue', label: `Overdue · ${plural(days, 'day')}` };
    case 'dueToday':
      return { tone: 'dueToday', label: 'Due today' };
    case 'upcoming':
      return { tone: 'upcoming', label: `Due in ${plural(days, 'day')}` };
    default:
      return { tone: 'none', label: 'No due date' };
  }
}

/**
 * Labels for the card button, named for where the desktop action for the same row actually goes, so
 * the label never promises more than the route delivers (a past-due row with no inspection opens the
 * project's inspections page, not an inspection).
 */

/** The 'Start Inspection' action, which runs the start handler rather than following a route. */
export function getStartActionLabel(row: DueRow): string {
  return row.inspectionStatus === 'new' && Number(row.inspectionId) > 0
    ? 'Continue inspection'
    : 'Start inspection';
}

/** Any action that navigates — a same-tenant <Link> or a cross-tenant redirect — by its route. */
export function getRouteActionLabel(route: string): string {
  if (route.endsWith('/questionnaire')) return 'Continue inspection';
  if (route.endsWith('/start-inspection')) return 'Start inspection';

  return 'View project';
}

/**
 * The full form name, which the phone card shows and "Form A–Z" sorts on. Deliberately not the desktop
 * Form column's `type || name`: the card has room for the name, and the story asks for it.
 */
export function getFormName(row: DueRow): string {
  return row.inspection_template_name || row.inspection_template_type || '—';
}

/** "Feb 24"; an unparseable value is shown as sent, as the desktop table shows it. */
export function formatDueDate(row: DueRow, parse: ParseDueDate): string {
  const due = parseDue(row, parse);

  if (due) return format(due, 'MMM d');

  return row.dueDate || '';
}

export function filterRows<T extends DueRow>(
  rows: T[],
  filter: FilterKey,
  parse: ParseDueDate,
  now: Date,
): T[] {
  if (filter === 'all') return rows;

  const bucket = filter === 'noDate' ? 'none' : filter;

  return rows.filter((row) => getDueBucket(row, parse, now).bucket === bucket);
}

export function countByFilter(
  rows: DueRow[],
  parse: ParseDueDate,
  now: Date,
): Record<FilterKey, number> {
  const counts: Record<FilterKey, number> = {
    all: rows.length,
    overdue: 0,
    dueToday: 0,
    upcoming: 0,
    noDate: 0,
  };

  rows.forEach((row) => {
    const { bucket } = getDueBucket(row, parse, now);

    counts[bucket === 'none' ? 'noDate' : bucket] += 1;
  });

  return counts;
}

const compareText = (a?: string | null, b?: string | null) =>
  (a || '').localeCompare(b || '', undefined, { sensitivity: 'base' });

/** Returns a sorted copy; the input array (context state) is never mutated. Undated rows sort last. */
export function sortRows<T extends DueRow>(rows: T[], sort: SortKey, parse: ParseDueDate): T[] {
  const copy = [...rows];

  if (sort === 'site') return copy.sort((a, b) => compareText(a.siteName, b.siteName));
  if (sort === 'client') return copy.sort((a, b) => compareText(a.clientName, b.clientName));
  if (sort === 'form') return copy.sort((a, b) => compareText(getFormName(a), getFormName(b)));

  const direction = sort === 'dueAsc' ? 1 : -1;

  return copy.sort((a, b) => {
    const dueA = parseDue(a, parse);
    const dueB = parseDue(b, parse);

    if (!dueA || !dueB) {
      return Number(!dueA) - Number(!dueB);
    }

    return (dueA.getTime() - dueB.getTime()) * direction;
  });
}
